import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Package, Camera, Tent, ArrowRight, Check, Shield, ShieldCheck, 
  Zap, Info, Video, Box, Lock, Award
} from 'lucide-react';
import { useBrand } from '../context/BrandContext';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';

const ICON_MAP: Record<string, any> = {
  Package,
  Camera,
  Tent
};

export default function VesselConfigurator() {
  const { products: allProducts, resolveMediaUrl } = useBrand();
  const itemsToShow = allProducts.filter(p => 
    p.name !== 'Basic Tire' && 
    (p.category?.toLowerCase() !== 'membership' || p.id === 'ows-cx2-board-preorder' || p.id === 'batch-001-membership')
  );
  const products = itemsToShow.length > 0 ? itemsToShow : allProducts;
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [selectedBundle, setSelectedBundle] = useState(products[0]);
  const [activeMediaIndex, setActiveMediaIndex] = useState(-1);

  useEffect(() => {
    if (products.length > 0 && !products.find(p => p.id === selectedBundle?.id)) {
      setSelectedBundle(products[0]);
      setActiveMediaIndex(-1);
    }
  }, [products]);

  useEffect(() => {
    setActiveMediaIndex(-1);
  }, [selectedBundle]);

  if (!selectedBundle) return null;

  const galleryItems = selectedBundle.gallery || [];
  const activeMedia: any = activeMediaIndex === -1 
    ? { type: 'image', url: selectedBundle.image } 
    : galleryItems[activeMediaIndex];

  const proceedToCheckout = () => {
    const depositAmount = (selectedBundle as any).depositPrice || 299.70;
    const updatedBundle = { 
      ...selectedBundle, 
      price: depositAmount, 
      salePrice: undefined 
    };
    addToCart(updatedBundle);
    navigate('/checkout');
  };

  return (
    <section className="py-20 bg-[#E4E3E0] min-h-screen text-[#141414] font-sans selection:bg-ows-teal selection:text-white relative" id="configurator">
      {/* Subtle Dossier Grid */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none" 
        style={{ backgroundImage: 'radial-gradient(#141414 1px, transparent 1px)', backgroundSize: '40px 40px' }} 
      />

      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Clean Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-[#141414]/15 pb-6 mb-10 gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-ows-teal animate-pulse" />
              <span className="text-[10px] font-mono font-bold text-ows-teal uppercase tracking-widest">BATCH 01 PRODUCTION SLOT</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-display uppercase tracking-tight leading-none text-[#141414]">
              PRE-ORDER <span className="text-ows-teal">OWS EXPEDITION CX2</span>
            </h2>
          </div>
          <div className="text-left md:text-right">
            <span className="text-[10px] font-mono text-[#141414]/50 uppercase tracking-widest block mb-0.5">ESTIMATED DISPATCH</span>
            <span className="text-xs font-mono font-bold text-[#141414] uppercase tracking-wider">MARCH 2027</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left Column: Media Gallery & 3 Trust Pillars */}
          <div className="lg:col-span-7 space-y-8">
            
            {/* Main Visual Display */}
            <div className="relative group rounded-3xl overflow-hidden bg-white/60 border border-[#141414]/10 aspect-video flex items-center justify-center shadow-sm">
              <div className="absolute top-5 left-5 flex flex-col gap-1.5 z-10">
                <span className="text-[9px] font-mono font-bold text-ows-teal border border-ows-teal/30 px-2.5 py-1 rounded-full uppercase tracking-widest bg-white/90 shadow-sm">
                  VESSEL INSPECTION
                </span>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={`${selectedBundle.id}-${activeMediaIndex}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full h-full relative"
                >
                  {activeMedia.type === 'video' ? (
                    <video 
                      src={resolveMediaUrl(activeMedia.url)}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover filter brightness-105"
                    />
                  ) : (
                    <img 
                      src={resolveMediaUrl(activeMedia.url)} 
                      alt={selectedBundle.name} 
                      className="w-full h-full object-cover filter brightness-105"
                      referrerPolicy="no-referrer"
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Gallery Thumbnails */}
            <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
              <button 
                onClick={() => setActiveMediaIndex(-1)}
                className={`flex-shrink-0 w-20 h-20 rounded-2xl border border-[#141414]/10 transition-all overflow-hidden bg-white p-2 ${
                  activeMediaIndex === -1 ? 'ring-2 ring-ows-teal scale-105 shadow-md' : 'opacity-50 hover:opacity-100'
                }`}
              >
                <img src={resolveMediaUrl(selectedBundle.image)} className="w-full h-full object-contain" alt="Main" />
              </button>
              {galleryItems.map((item, idx) => (
                <button 
                  key={`thumb-${idx}`}
                  onClick={() => setActiveMediaIndex(idx)}
                  className={`flex-shrink-0 w-20 h-20 rounded-2xl border border-[#141414]/10 transition-all overflow-hidden bg-white p-2 relative ${
                    activeMediaIndex === idx ? 'ring-2 ring-ows-teal scale-105 shadow-md' : 'opacity-50 hover:opacity-100'
                  }`}
                >
                  {item.type === 'video' ? (
                    <div className="w-full h-full bg-[#141414] flex items-center justify-center rounded-xl">
                      <Video className="h-5 w-5 text-white" />
                    </div>
                  ) : (
                    <img src={resolveMediaUrl(item.url)} className="w-full h-full object-contain" alt={`Gallery ${idx}`} />
                  )}
                </button>
              ))}
            </div>

            {/* High-Trust Guarantees (Replaces heavy roadmap) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <div className="p-5 bg-white/70 border border-[#141414]/10 rounded-2xl space-y-2">
                <div className="w-8 h-8 rounded-xl bg-ows-teal/10 flex items-center justify-center text-ows-teal">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-[#141414]">100% Refund Guarantee</h3>
                <p className="text-[11px] font-sans text-[#141414]/70 leading-relaxed">
                  If Batch 01 production threshold is not reached, your $299.70 CAD deposit is automatically refunded in full.
                </p>
              </div>

              <div className="p-5 bg-white/70 border border-[#141414]/10 rounded-2xl space-y-2">
                <div className="w-8 h-8 rounded-xl bg-ows-teal/10 flex items-center justify-center text-ows-teal">
                  <Award className="h-4 w-4" />
                </div>
                <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-[#141414]">Expedition-Validated</h3>
                <p className="text-[11px] font-sans text-[#141414]/70 leading-relaxed">
                  Tested over 110km+ open water crossings on Lake Ontario and Baja California. Built for high-stakes reliability.
                </p>
              </div>

              <div className="p-5 bg-white/70 border border-[#141414]/10 rounded-2xl space-y-2">
                <div className="w-8 h-8 rounded-xl bg-ows-teal/10 flex items-center justify-center text-ows-teal">
                  <Box className="h-4 w-4" />
                </div>
                <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-[#141414]">Direct Factory Dispatch</h3>
                <p className="text-[11px] font-sans text-[#141414]/70 leading-relaxed">
                  Fulfillment verified and dispatched directly from our Toronto logistics center with full tracking.
                </p>
              </div>
            </div>

          </div>

          {/* Right Column: High-Conversion Order Terminal */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Main Order Card */}
            <div className="p-5 sm:p-8 bg-white border border-[#141414]/15 rounded-[1.5rem] sm:rounded-[2rem] shadow-xl space-y-6">
              
              {/* Early Pre-Order Lock Badge */}
              <div className="p-3 bg-ows-teal/10 border border-ows-teal/30 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-2 text-ows-teal">
                  <Zap className="h-4 w-4 shrink-0" />
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider">Batch 01 Early Lock</span>
                </div>
                <span className="text-[10px] font-mono font-bold text-[#141414] uppercase shrink-0">Save $300 CAD</span>
              </div>

              {/* Price & Deposit Focus */}
              <div className="space-y-3 border-b border-[#141414]/10 pb-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-baseline gap-3 sm:gap-2">
                  <div>
                    <span className="text-[9px] font-mono font-bold text-[#141414]/50 uppercase tracking-widest block">DUE TODAY (30% DEPOSIT)</span>
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-3xl sm:text-4xl md:text-5xl font-display text-[#141414] font-bold">$299.70 CAD</span>
                      <span className="text-xs font-mono text-ows-teal font-bold">(~$220 USD)</span>
                    </div>
                  </div>
                  <div className="text-left sm:text-right">
                    <span className="text-[9px] font-mono text-[#141414]/50 uppercase tracking-widest block">TOTAL PRICE</span>
                    <span className="text-lg sm:text-xl font-mono font-bold text-[#141414]">$999 CAD</span>
                    <span className="text-[10px] font-mono text-[#141414]/40 line-through block">$1,299 MSRP</span>
                  </div>
                </div>

                <p className="text-xs font-sans text-[#141414]/70 leading-relaxed">
                  Secures your Batch 01 build slot today. The remaining 70% balance ($699.30 CAD / ~$515 USD) is billed before dispatch.
                </p>
              </div>

              {/* Complete System Gear Included */}
              <div className="space-y-3">
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#141414]/60 block">
                  COMPLETE EXPEDITION KIT INCLUDED
                </span>
                
                <ul className="space-y-2.5 text-xs font-sans text-[#141414]/85">
                  <li className="flex items-start gap-2.5">
                    <div className="w-4 h-4 rounded-full bg-ows-teal/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-2.5 w-2.5 text-ows-teal" />
                    </div>
                    <span><strong>1× OWS Expedition CX2 Vessel</strong> (12'6" × 32" × 6", V-Hull & Dual Air Chamber)</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <div className="w-4 h-4 rounded-full bg-ows-teal/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-2.5 w-2.5 text-ows-teal" />
                    </div>
                    <span><strong>1× 4-Section Carbon Fiber Paddle</strong></span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <div className="w-4 h-4 rounded-full bg-ows-teal/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-2.5 w-2.5 text-ows-teal" />
                    </div>
                    <span><strong>1× Waterproof Drybag Backpack</strong> with integrated solar panel</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <div className="w-4 h-4 rounded-full bg-ows-teal/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-2.5 w-2.5 text-ows-teal" />
                    </div>
                    <span><strong>1× Coiled Safety Leash & US Box Tri-Fin Set</strong> (Includes one 9" primary center fin for deep tracking + two side fins for shallow water draft & tracking control; all 100% removable)</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <div className="w-4 h-4 rounded-full bg-ows-teal/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-2.5 w-2.5 text-ows-teal" />
                    </div>
                    <span><strong>1× Field Emergency Repair Kit</strong></span>
                  </li>
                </ul>
              </div>

              {/* Call to Action Button */}
              <div className="space-y-3 pt-2">
                <button 
                  onClick={proceedToCheckout}
                  className="w-full bg-ows-teal text-white py-5 rounded-2xl font-mono font-bold text-xs hover:bg-[#141414] transition-all flex items-center justify-center gap-3 uppercase tracking-[0.25em] shadow-lg hover:shadow-xl group"
                >
                  <Shield className="h-4 w-4" />
                  <span>PRE-ORDER NOW ($299.70 CAD DEPOSIT)</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1.5 transition-transform" />
                </button>

                {/* Trust Footer */}
                <div className="flex items-center justify-center gap-2 text-[10px] font-mono text-[#141414]/50">
                  <Lock className="h-3 w-3 text-ows-teal" />
                  <span>Secure Checkout • Instant Confirmation • 100% Refundable</span>
                </div>
              </div>

            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
